import { MapPin } from "lucide-react";
import { Link } from "react-router";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SellerAvatar } from "./SellerAvatar";
import { StarRating } from "./StarRating";
import {
  type Meeting,
  formatMeetingDate,
  formatMeetingTime,
  formatMoneyMx,
  initials,
  linesToList,
  findSectorLabel,
  findStageLabel,
} from "./meetingsUtils";

/**
 * MeetingCard — copia fiel del prototipo P3 (`card-m`).
 *
 * Recibe un Meeting (proveniente de `meetings_summary` view) y renderea
 * la tarjeta bento con: company logo, fecha, wave-strip, topic y footer
 * con pills + amount.
 *
 * `variant="featured"` activa el gradiente amarillo + span 2 columnas.
 */
export function MeetingCard({
  meeting,
  variant = "default",
}: {
  meeting: Meeting;
  variant?: "default" | "featured";
}) {
  const { companySectors, dealStages } = useConfigurationContext();
  const topics = linesToList(meeting.topics);
  const firstTopic = topics[0] || meeting.summary || meeting.title || "Sin temas";
  const restCount = Math.max(0, topics.length - 1);
  const logo = (
    meeting.company_logo?.title?.charAt(0) ||
    meeting.company_name?.charAt(0) ||
    "?"
  ).toUpperCase();
  const sector = findSectorLabel(companySectors, meeting.company_sector);
  const stage = findStageLabel(dealStages, meeting.deal_stage);
  const isFeatured = variant === "featured";
  const hasRating = typeof meeting.rating === "number" && meeting.rating > 0;
  const location = meeting.location || meeting.company_city || "";

  return (
    <Link
      to={`/meetings/${meeting.id}/show`}
      className={"musco-card-m" + (isFeatured ? " featured" : "")}
      aria-label={`Junta con ${meeting.company_name}`}
    >
      <div className="flex items-start gap-3.5">
        <div className="logo-sq">{logo}</div>
        <div className="flex-1 min-w-0">
          <div className="co truncate">
            {meeting.company_name || "Empresa"}
          </div>
          <div className="sub truncate">
            {sector}
            {location ? ` · ${location}` : ""}
          </div>
        </div>
        <div className="date-mini">
          <div className="d">{formatMeetingDate(meeting.occurred_at)}</div>
          <div>{formatMeetingTime(meeting.occurred_at)}</div>
        </div>
      </div>

      {/* Rating strip — reemplaza la antigua wave-strip de audio */}
      <div
        className="rounded-xl px-3 py-2.5 flex items-center gap-3"
        style={{
          background: isFeatured ? "rgba(10,10,10,0.08)" : "var(--musco-panel2)",
        }}
      >
        <StarRating
          value={meeting.rating ?? 0}
          size={isFeatured ? 18 : 16}
          readOnly
          color={isFeatured ? "#0a0a0a" : "var(--musco-accent)"}
        />
        <span
          className="text-xs font-semibold tnums"
          style={{
            color: isFeatured ? "rgba(10,10,10,0.7)" : "var(--musco-muted)",
          }}
        >
          {hasRating ? `${meeting.rating}/5` : "Sin calificar"}
        </span>
        {location ? (
          <span
            className="text-xs ml-auto flex items-center gap-1 truncate min-w-0"
            style={{
              color: isFeatured ? "rgba(10,10,10,0.7)" : "var(--musco-muted)",
              maxWidth: "55%",
            }}
            title={location}
          >
            <MapPin size={11} />
            <span className="truncate">{location}</span>
          </span>
        ) : null}
      </div>

      <div className="topic">
        <span className="line-clamp-1">
          {firstTopic}
          {restCount > 0 ? (
            <em> · {restCount} tema{restCount > 1 ? "s" : ""} más</em>
          ) : null}
        </span>
      </div>

      <div className="footer">
        {isFeatured ? (
          <span
            className="musco-pill"
            style={{
              background: "rgba(10,10,10,0.92)",
              color: "var(--musco-accent)",
              fontWeight: 600,
            }}
          >
            ★ DESTACADA
          </span>
        ) : null}
        {meeting.deal_stage ? (
          <span className="musco-pill">{stage}</span>
        ) : null}
        <SellerAvatar
          id={meeting.sales_id}
          name={meeting.sales_name}
          size={isFeatured ? 26 : 24}
          imageUrl={meeting.sales_avatar?.src}
        />
        {typeof meeting.deal_amount === "number" ? (
          <span className="amount-pill">{formatMoneyMx(meeting.deal_amount)}</span>
        ) : null}
      </div>
    </Link>
  );
}

/** Skeleton minimal — para Suspense fallback de la lista. */
export function MeetingCardSkeleton({ featured = false }: { featured?: boolean }) {
  return (
    <div
      className={"musco-card-m" + (featured ? " featured" : "")}
      style={{ pointerEvents: "none", opacity: 0.6 }}
    >
      <div className="flex items-start gap-3.5">
        <div className="logo-sq">·</div>
        <div className="flex-1">
          <div className="co">···</div>
          <div className="sub">cargando…</div>
        </div>
      </div>
      <div
        className="rounded-xl px-3 py-2.5"
        style={{ background: "var(--musco-panel2)", minHeight: 36 }}
      />
      <div className="topic">···</div>
      <div className="footer">
        <span className="musco-pill">···</span>
      </div>
    </div>
  );
}

/* Tiny helper para que TypeScript no se queje de initials no usado. */
void initials;
